import { useState } from "react";
import type {
  AdultGigRequestListItemResponse,
  AdultGigRequestTeenDetailResponse,
} from "../../../types/gigTypes";
import { resolveGigTimeZone } from "../../../utils/DateUtils";
import { formatCityStateZip, formatDistanceLabel } from "../../../utils/LocationUtils";
import HoverText from "../../ui/HoverText/HoverText";
import InfoTooltip from "../../ui/InfoTooltip/InfoTooltip";
import Modal from "../../ui/Modal/Modal";
import {
  pendingRequestStatusClassName,
  pendingRequestStatusLabel,
  toReadableDate,
} from "../dashboard/shared/dashboardDisplay";
import { formatDateRangeWithWeekdays, formatTeenAge, getTeenName } from "./gigDisplay";

const toDaysAgoLabel = (value: string): string | null => {
  const appliedAt = new Date(value);
  if (Number.isNaN(appliedAt.getTime())) {
    return null;
  }

  const now = new Date();
  const diffMs = now.getTime() - appliedAt.getTime();
  if (diffMs < 0) {
    return null;
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) {
    return "Today";
  }
  if (days === 1) {
    return "1 day ago";
  }
  return `${days} days ago`;
};

const formatCompactLocation = (teenDetail?: AdultGigRequestTeenDetailResponse, isLoading = false): string => {
  if (teenDetail) {
    const city = teenDetail.city?.trim() ?? "";
    const state = teenDetail.state?.trim() ?? "";
    return [city, state].filter(Boolean).join(", ") || "Unknown";
  }

  return isLoading ? "Loading" : "Unavailable";
};

const formatPreferredStartParts = (
  value: string | null,
  timeZone?: string | null,
): { dateLabel: string; timeLabel: string | null } => {
  if (!value) {
    return { dateLabel: "No date", timeLabel: null };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { dateLabel: "No date", timeLabel: null };
  }

  const resolvedTimeZone = resolveGigTimeZone(timeZone);
  return {
    dateLabel: new Intl.DateTimeFormat(undefined, {
      timeZone: resolvedTimeZone,
      month: "short",
      day: "numeric",
    }).format(parsed),
    timeLabel: new Intl.DateTimeFormat(undefined, {
      timeZone: resolvedTimeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(parsed),
  };
};

type TeenRequestRowProps = {
  gigId: string;
  request: AdultGigRequestListItemResponse;
  teenDetail?: AdultGigRequestTeenDetailResponse;
  isTeenDetailLoading?: boolean;
  isHighlighted?: boolean;
  requiresFunding: boolean;
  isApproving: boolean;
  isRejecting: boolean;
  isScheduling: boolean;
  selectsBeforePayment?: boolean;
  onAcceptTeen: (gigId: string, request: AdultGigRequestListItemResponse) => Promise<void>;
  onRejectTeen: (gigId: string, request: AdultGigRequestListItemResponse) => void;
  onViewTeen: (gigId: string, request: AdultGigRequestListItemResponse) => void;
  onOpenConversation: (request: AdultGigRequestListItemResponse) => void;
};

const TeenRequestRow = ({
  gigId,
  request,
  teenDetail,
  isTeenDetailLoading = false,
  isHighlighted = false,
  requiresFunding,
  isApproving,
  isRejecting,
  isScheduling,
  selectsBeforePayment = false,
  onAcceptTeen,
  onRejectTeen,
  onViewTeen,
  onOpenConversation,
}: TeenRequestRowProps) => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const isGuardianPending = request.status === "GUARDIAN_PENDING";
  const isSelectedPendingCrew = request.status === "POSTER_SELECTED_PENDING_CREW";
  const isPayoutSetupPending = request.status === "PAYOUT_SETUP_PENDING";
  const isFundingPending = request.status === "FUNDING_PENDING";
  const isWaitingOnTeenScheduleReply = request.status === "POSTER_PENDING"
    && request.latestProposalByRole === "POSTER"
    && request.teenConfirmedPosterTimeAt === null;
  const isWaitingOnTeenReschedule = request.requiresScheduleReproposal;
  const isWaitingOnTeenAcknowledgment = request.requiresGigChangeAcknowledgment;
  const canStartFundingOrAccept = request.status === "POSTER_PENDING" || isFundingPending;
  const acceptDisabled = !canStartFundingOrAccept
    || isApproving
    || isWaitingOnTeenScheduleReply
    || isWaitingOnTeenReschedule
    || isWaitingOnTeenAcknowledgment;
  const rejectDisabled = !["POSTER_PENDING", "POSTER_SELECTED_PENDING_CREW", "PAYOUT_SETUP_PENDING", "FUNDING_PENDING"].includes(request.status) || isRejecting;
  const teenName = getTeenName(request);
  const teenInitial = teenName.trim().charAt(0).toUpperCase() || "T";
  const teenFirstName = request.teenFirstName?.trim() || teenName.split(" ")[0] || "Teen";
  const appliedDaysAgoLabel = toDaysAgoLabel(request.createdAt);
  const ageLabel = teenDetail ? formatTeenAge(teenDetail.dateOfBirth) : isTeenDetailLoading ? "Loading" : "Unknown";
  const ratingLabel = teenDetail
    ? typeof teenDetail.averageRating === "number" && teenDetail.totalReviews > 0
      ? `${teenDetail.averageRating.toFixed(1)} (${teenDetail.totalReviews})`
      : "New"
    : isTeenDetailLoading
      ? "Loading"
      : "New";
  const profilePhoto = teenDetail?.profilePhoto ?? null;
  const preferredStart = formatPreferredStartParts(request.proposedStartTime, request.gigTimeZone);
  const compactLocationLabel = formatCompactLocation(teenDetail, isTeenDetailLoading);
  const locationLabel = teenDetail
    ? [
      formatCityStateZip(teenDetail, "Unknown"),
      formatDistanceLabel({
        distanceMiles: teenDetail.distanceMilesFromGig,
        zip: teenDetail.zip,
        comparisonZip: teenDetail.gigZip,
      }),
    ].filter(Boolean).join(" - ")
    : isTeenDetailLoading
      ? "Loading profile..."
      : "Profile details unavailable";
  const skills = teenDetail?.skills ?? [];
  const acceptDisabledMessage = isGuardianPending
    ? "The parent or guardian must approve before you can accept this teen."
    : isSelectedPendingCrew
      ? "This applicant is selected while the crew is finalized."
    : isPayoutSetupPending
      ? "This teen is selected conditionally. You can pay to confirm after payout setup is ready."
    : isWaitingOnTeenReschedule
      ? "Your gig update changed the schedule window. This teen needs to send a new start time before you can accept them."
      : isWaitingOnTeenAcknowledgment
        ? "The teen must acknowledge your latest gig update before you can accept this request."
    : isWaitingOnTeenScheduleReply
      ? "The teen must respond to or confirm your latest proposed time before you can accept this request."
      : "";
  const acceptButtonLabel = (() => {
    if (isSelectedPendingCrew) {
      return "Selected";
    }
    if (request.status === "POSTER_ACCEPTED") {
      return "Accepted";
    }
    if (isApproving) {
      if (selectsBeforePayment) {
        return "Selecting...";
      }
      return requiresFunding ? "Starting..." : "Accepting...";
    }
    if (selectsBeforePayment) {
      return "Select";
    }
    if (!requiresFunding) {
      return "Accept";
    }
    if (isFundingPending) {
      return "Pay to confirm";
    }
    if (isPayoutSetupPending) {
      return "Waiting on payout";
    }
    return request.teenPayoutReady ? "Accept & fund" : "Select pending payout";
  })();
  const rejectButtonLabel = isSelectedPendingCrew || isPayoutSetupPending || isFundingPending
    ? isRejecting ? "Removing..." : "Remove selection"
    : isRejecting ? "Rejecting..." : "Reject";

  return (
    <article id={`adult-teen-request-${request.requestId}`} className="min-w-0 scroll-mt-24">
      <div className={`min-w-0 rounded-2xl border border-myteengig-border bg-white px-4 py-4 shadow-sm ${isHighlighted ? "ring-2 ring-myteengig-primary/35" : ""}`}>
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-myteengig-border bg-myteengig-background text-sm font-semibold text-myteengig-text-primary sm:h-14 sm:w-14">
            {profilePhoto ? (
              <img src={profilePhoto.url} alt={`${teenName} profile`} className="h-full w-full object-cover" />
            ) : (
              <span>{teenInitial}</span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold text-myteengig-text-primary">{teenFirstName}</p>
            <span
              className={`mt-1 inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-tight ${pendingRequestStatusClassName[request.status]}`}
            >
              <span className="truncate">{pendingRequestStatusLabel[request.status]}</span>
            </span>
          </div>

          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => setIsDetailsModalOpen(true)}
            className="inline-flex h-9 shrink-0 items-center justify-center rounded-full border border-myteengig-border bg-white px-3 text-xs font-semibold text-myteengig-text-primary transition-colors hover:border-myteengig-primary hover:text-myteengig-primary"
          >
            More
          </button>
        </div>

        {requiresFunding && request.status === "POSTER_PENDING" && !request.teenPayoutReady ? (
          <div className="mt-3 rounded-2xl border border-sky-200 bg-sky-50 px-3 py-3 text-xs leading-5 text-sky-900">
            You can select this teen now. No payment is collected until their payout setup is complete.
          </div>
        ) : null}

        {isPayoutSetupPending ? (
          <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-900">
            Selected conditionally. The gig is not fully confirmed or funded until payout setup is complete.
          </div>
        ) : null}

        {isFundingPending ? (
          <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs leading-5 text-emerald-900">
            Payout setup is ready. Pay now to fully confirm this gig.
          </div>
        ) : null}

        <div className="mt-3 grid min-w-0 grid-cols-2 gap-x-4 gap-y-2 text-xs text-myteengig-text-primary sm:grid-cols-4">
          <div className="min-w-0">
            <p className="text-myteengig-text-muted">Age</p>
            <p className="truncate font-semibold">{ageLabel}</p>
          </div>
          <div className="min-w-0">
            <p className="text-myteengig-text-muted">Rating</p>
            <p className="truncate font-semibold">{ratingLabel}</p>
          </div>
          <div className="min-w-0">
            <p className="text-myteengig-text-muted">Location</p>
            <p className="truncate font-semibold">{compactLocationLabel}</p>
          </div>
          <div className="min-w-0 sm:text-center">
            <div className="flex items-center gap-1 text-myteengig-text-muted sm:justify-center">
              <p className="whitespace-nowrap">Preferred start</p>
              <InfoTooltip
                label="Preferred start info"
                message="This is when the applicant prefers to start the gig."
                placement="top-right"
                className="-my-2"
                panelClassName="font-medium"
              />
            </div>
            <div className="font-semibold leading-tight text-myteengig-text-primary">
              <p>{preferredStart.dateLabel}</p>
              {preferredStart.timeLabel ? <p>{preferredStart.timeLabel}</p> : null}
            </div>
          </div>
        </div>

        <div className="mt-3 grid min-w-0 gap-2">
          <button
            type="button"
            onClick={() => onOpenConversation(request)}
            className="inline-flex h-10 w-full items-center justify-center rounded-full border border-myteengig-border bg-white px-3 text-sm font-semibold text-myteengig-text-primary transition-colors hover:border-myteengig-primary hover:text-myteengig-primary"
          >
            {isScheduling ? "Opening..." : "Message / Suggest Time"}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <HoverText
              isEnabled={acceptDisabledMessage.length > 0}
              placement="top-left"
              message={acceptDisabledMessage}
              className="min-w-0"
            >
              <button
                type="button"
                onClick={() => void onAcceptTeen(gigId, request)}
                disabled={acceptDisabled}
                className="inline-flex h-10 w-full items-center justify-center rounded-full bg-myteengig-primary px-3 text-center text-sm font-semibold leading-tight text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
              >
                {acceptButtonLabel}
              </button>
            </HoverText>
            <button
              type="button"
              onClick={() => onRejectTeen(gigId, request)}
              disabled={rejectDisabled}
              className="inline-flex h-10 w-full items-center justify-center rounded-full border border-rose-200 bg-rose-50 px-3 text-center text-sm font-semibold leading-tight text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {rejectButtonLabel}
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title={`${teenName} Details`}
        panelClassName="max-w-4xl"
      >
        <div className="space-y-3">
          <div className="grid gap-3 text-sm md:grid-cols-3">
            <div className="rounded-xl bg-myteengig-background px-3 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Profile</p>
              <p className="mt-1 font-semibold text-myteengig-text-primary">{teenName}</p>
              <p className="mt-1 text-myteengig-text-muted">{locationLabel}</p>
              <p className="mt-1 text-myteengig-text-muted">
                Applied: {toReadableDate(request.createdAt)}{appliedDaysAgoLabel ? ` (${appliedDaysAgoLabel})` : ""}
              </p>
              <p className="mt-1 text-myteengig-text-muted">
                Completed gigs: {teenDetail ? teenDetail.completedGigCount : isTeenDetailLoading ? "Loading" : "Unknown"}
              </p>
            </div>

            <div className="rounded-xl bg-myteengig-background px-3 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Preferred date and time</p>
              <p className="mt-1 font-semibold text-myteengig-text-primary">
                {formatDateRangeWithWeekdays(request.proposedStartTime, request.proposedEndTime, request.gigTimeZone)}
              </p>
              {request.teenConfirmedPosterTimeAt ? (
                <p className="mt-2 text-xs font-medium text-emerald-700">
                  {teenFirstName} confirmed your latest suggested time.
                </p>
              ) : null}
              {request.requiresScheduleReproposal ? (
                <p className="mt-2 text-xs font-medium text-amber-700">
                  Your gig update changed the schedule window. This applicant needs to send a new start time.
                </p>
              ) : null}
              {request.requiresGigChangeAcknowledgment ? (
                <p className="mt-2 text-xs font-medium text-sky-700">
                  This request is waiting on the applicant to acknowledge your latest gig update.
                </p>
              ) : null}
            </div>

            <div className="rounded-xl bg-myteengig-background px-3 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Message</p>
              <p className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap text-myteengig-text-primary">
                {request.message?.trim() || "No message included."}
              </p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="rounded-xl bg-myteengig-background px-3 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">About and skills</p>
              <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-sm text-myteengig-text-primary">
                {teenDetail?.bio?.trim() || (isTeenDetailLoading ? "Loading profile details..." : "No self-description added yet.")}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {skills.length > 0 ? (
                  skills.slice(0, 6).map((skill) => (
                    <span key={skill} className="rounded-full border border-myteengig-border bg-white px-2.5 py-1 text-xs font-semibold text-myteengig-text-primary">
                      {skill}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-myteengig-text-muted">
                    {isTeenDetailLoading ? "Loading skills..." : "No skills added yet."}
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setIsDetailsModalOpen(false);
                onViewTeen(gigId, request);
              }}
              className="inline-flex h-10 w-full items-center justify-center rounded-full border border-myteengig-border bg-white px-4 text-sm font-semibold text-myteengig-text-primary transition-colors hover:border-myteengig-primary hover:text-myteengig-primary md:w-auto"
            >
              Open full profile
            </button>
          </div>
        </div>
      </Modal>
    </article>
  );
};

export default TeenRequestRow;
